import { initializeApp } from "firebase/app"
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithCredential,
} from "firebase/auth"
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore"
import { getItemFromLocalStorage, setItemOnLocalStorage, removeItemOnLocalStorage } from "../helpers/utils"

let app = null
let auth = null
let db = null

class FirebaseAuthBackend {
  constructor(firebaseConfig) {
    if (firebaseConfig) {
      // Initialize Firebase
      app = initializeApp(firebaseConfig)
      auth = getAuth(app)
      db = getFirestore(app)

      onAuthStateChanged(auth, user => {
        if (user) {
          setItemOnLocalStorage("user_id", JSON.stringify(user))
        } else {
          removeItemOnLocalStorage("user_id")
        }
      })
    }
  }

  /**
   * Registers the user with given details
   */
  registerUser = (email, password) => {
    return new Promise((resolve, reject) => {
      createUserWithEmailAndPassword(auth, email, password)
        .then(
          userCredential => {
            resolve(userCredential.user)
          },
          error => {
            reject(this._handleError(error))
          }
        )
    })
  }

  /**
   * Registers the user with given details
   */
  editProfileAPI = (email, password) => {
    return new Promise((resolve, reject) => {
      createUserWithEmailAndPassword(auth, email, password)
        .then(
          userCredential => {
            resolve(userCredential.user)
          },
          error => {
            reject(this._handleError(error))
          }
        )
    })
  }

  /**
   * Login user with given details
   */
  loginUser = (email, password) => {
    return new Promise((resolve, reject) => {
      signInWithEmailAndPassword(auth, email, password)
        .then(
          userCredential => {
            resolve(userCredential.user)
          },
          error => {
            reject(this._handleError(error))
          }
        )
    })
  }

  /**
   * forget Password user with given details
   */
  forgetPassword = email => {
    return new Promise((resolve, reject) => {
      sendPasswordResetEmail(auth, email, {
        url:
          window.location.protocol + "//" + window.location.host + "/login",
      })
        .then(() => {
          resolve(true)
        })
        .catch(error => {
          reject(this._handleError(error))
        })
    })
  }

  /**
   * Logout the user
   */
  logout = () => {
    return new Promise((resolve, reject) => {
      signOut(auth)
        .then(() => {
          resolve(true)
        })
        .catch(error => {
          reject(this._handleError(error))
        })
    })
  }

  /**
   * Social Login user with given details
   */
  socialLoginUser = (data, type) => {
    let credential = null
    if (type === "google") {
      credential = GoogleAuthProvider.credential(data.idToken, data.token)
    } else if (type === "facebook") {
      credential = FacebookAuthProvider.credential(data.token)
    }
    return new Promise((resolve, reject) => {
      if (credential) {
        signInWithCredential(auth, credential)
          .then(userCredential => {
            resolve(this.addNewUserToFirestore(userCredential))
          })
          .catch(error => {
            reject(this._handleError(error))
          })
      } else {
        reject("Invalid credential type")
      }
    })
  }

  addNewUserToFirestore = (userCredential) => {
    const usersCollection = collection(db, "users")
    const { profile } = userCredential.additionalUserInfo || {}
    const details = {
      firstName: profile?.given_name ? profile.given_name : profile?.first_name || "",
      lastName: profile?.family_name ? profile.family_name : profile?.last_name || "",
      fullName: profile?.name || "",
      email: profile?.email || "",
      picture: profile?.picture || "",
      createdDtm: serverTimestamp(),
      lastLoginTime: serverTimestamp()
    }
    const userDoc = doc(usersCollection, userCredential.user.uid)
    setDoc(userDoc, details)
    return { user: userCredential.user, details }
  }

  setLoggeedInUser = user => {
    setItemOnLocalStorage("user_id", JSON.stringify(user))
  }

  /**
   * Returns the authenticated user
   */
  getAuthenticatedUser = () => {
    if (!getItemFromLocalStorage("user_id")) return null
    return JSON.parse(getItemFromLocalStorage("user_id"))
  }

  /**
   * Handle the error
   * @param {*} error
   */
  _handleError(error) {
    var errorMessage = error.message
    return errorMessage
  }
}

let _fireBaseBackend = null

/**
 * Initilize the backend
 * @param {*} config
 */
const initFirebaseBackend = config => {
  if (!_fireBaseBackend) {
    _fireBaseBackend = new FirebaseAuthBackend(config)
  }
  return _fireBaseBackend
}

/**
 * Returns the firebase backend
 */
const getFirebaseBackend = () => {
  return _fireBaseBackend
}

export { initFirebaseBackend, getFirebaseBackend }
